import Vue from 'vue';
import Router from 'vue-router';
import fetchSubject from '@/middleware/fetch-subject';
import fetchLessons from '@/middleware/fetch-lessons';

// import fetchClusters from '@/middleware/fetch-clusters';

Vue.use(Router);

/**
 * constantRoutes
 * a base page that does not have permission requirements
 * all roles can be accessed
 */
export const constantRoutes = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/login/index.vue'),
    hidden: true,
    meta: {
      backgroundColor: 'yellow-blue',
    },
  },
  {
    path: '/404',
    component: () => import('@/views/NotFound.vue'),
    hidden: true,
  },
  {
    path: '/reset-password',
    name: 'resetPassword',
    component: () => import('@/views/ResetPassword.vue'),
    hidden: true,
  },
];

/**
 * asyncRoutes
 * the routes that need to be dynamically loaded based on user roles
 */
export const asyncRoutes = [
  {
    path: '/',
    name: 'home',
    component: () => import('@/views/levels/index.vue'),
    meta: {
      roles: ['student'],
      icon: 'home.png',
      i18n: 'home',
      name: 'Home',
      backgroundColor: 'white',
      layout: 'Student',
    },
  },
  {
    // add :level id for changes in css
    path: '/level/grade/:grade_id/:level',
    name: 'grade',
    component: () => import('@/views/grade/index.vue'),
    hidden: true,
    meta: {
      roles: ['student'],
      backgroundColor: 'orange',
      layout: 'Student',
      breadcrumb: { name: 'home' },
    },
  },
  {
    //add: level
    path: '/level/grade/:grade_id/:level/subject/:subject_id',
    name: 'subject',
    component: () => import('@/views/subject/index.vue'),
    hidden: true,
    meta: {
      roles: ['student'],
      middleware: [fetchSubject],
      backgroundColor: 'orange',
      layout: 'Student',
      breadcrumb: { name: 'grade' },
    },
    // children: [
    //   {
    //     path: ':cluster_id',
    //     name: 'showCluster',
    //     component: () => import('@/views/cluster/show.vue'),
    //     meta: {
    //       roles: ['student'],
    //       backgroundColor: 'orange',
    //       layout: 'Student',
    //       middleware: [fetchSubject],
    //     },
    //   },
    // ],
  },
  {
    path: '/level/grade/:grade_id/:level/subject/:subject_id/cluster/:cluster_id/lesson/:lesson_id',
    name: 'lesson',
    component: () => import('@/views/lesson/index.vue'),
    hidden: true,
    meta: {
      roles: ['student'],
      middleware: [fetchLessons],
      backgroundColor: 'white',
      layout: 'Student',
      //se comenta breadcrumb porque ya hay un boton exit en lesson
      //breadcrumb: { name: 'subject' },
    },
  },
  {
    path: '/grade/:grade_id/subject/:subject_id/cluster/:cluster_id/lesson/:lesson_id/quiz',
    name: 'quiz',
    component: () => import('@/views/quiz/index.vue'),
    hidden: true,
    meta: {
      roles: ['student'],
      layout: 'Student',
    },
    children: [
      {
        path: 'instructions',
        name: 'quizInstructions',
        component: () => import('@/views/quiz/instructions/index.vue'),
        meta: {
          roles: ['student'],
          layout: 'Student',
        },
      },
      {
        path: 'quiz-student',
        name: 'quizStart',
        component: () => import('@/views/quiz/quiz.vue'),
        meta: {
          roles: ['student'],
          layout: 'Student',
          backgroundColor: 'white',
          middleware: [fetchSubject],
        },
      },
      {
        path: 'results',
        name: 'quizResults',
        component: () => import('@/views/quiz/results/index.vue'),
        meta: {
          roles: ['student'],
          layout: 'Student',
        },
      },
    ],
  },
  {
    path: '/level/grade/:grade_id/:level/subject/:subject_id/cluster/:cluster_id/test',
    name: 'test',
    component: () => import('@/views/test/index.vue'),
    hidden: true,
    meta: {
      roles: ['student'],
      layout: 'Student',
    },
    children: [
      {
        path: 'instructions',
        name: 'testInstructions',
        component: () => import('@/views/test/instructions.vue'),
        meta: {
          roles: ['student'],
          backgroundColor: 'test-instructions',
          layout: 'Student',
        },
      },
      {
        path: 'test-student',
        name: 'testStart',
        component: () => import('@/views/test/test.vue'),
        meta: {
          roles: ['student'],
          backgroundColor: 'white',
          layout: 'Student',
        },
      },
      {
        path: 'results/:test_id',
        name: 'testResults',
        component: () => import('@/views/test/results/index.vue'),
        meta: {
          roles: ['student'],
          backgroundColor: 'white',
          layout: 'Student',
          middleware: [fetchSubject],
        },
      },
      {
        path: 'games',
        name: 'games',
        component: () => import('@/views/games/index.vue'),
        hidden: true,
        meta: {
          roles: ['student'],
          layout: 'Student',
        },
      },
    ],
  },
  {
    path: '/book',
    name: 'book',
    component: () => import('@/views/book/index.vue'),
    redirect: {
      name: 'library',
    },
    meta: {
      roles: ['student'],
      icon: 'books.png',
      i18n: 'books',
      name: 'e-library',
      layout: 'Student',
    },
    children: [
      {
        path: 'library',
        name: 'library',
        component: () => import('@/views/book/library.vue'),
        meta: {
          roles: ['student'],
          backgroundColor: 'cyan',
          layout: 'Student',
          breadcrumb: { name: 'home' },
        },
      },
      {
        path: 'show/:diskname/:zip_file',
        name: 'bookShow',
        component: () => import('@/views/book/show.vue'),
        meta: {
          roles: ['student'],
          layout: 'Student',
          breadcrumb: { name: 'library' },
        },
      },
    ],
  },
  {
    path: '/spelling-helper-dictionary',
    name: 'spelling-helper-dictionary',
    component: () => import('@/views/spelling-helper-dictionary/index.vue'),
    meta: {
      roles: ['student'],
      icon: 'dictionary.svg',
      i18n: 'spellingHelperDictionary',
      name: 'Spelling Helper Dictionary',
      layout: 'Student',
      breadcrumb: { name: 'home' },
    },
  },
  {
    path: '/help',
    name: 'help',
    component: () => import('@/views/help/index.vue'),
    meta: {
      roles: ['student'],
      icon: 'info.svg',
      backgroundColor: 'white',
      i18n: 'sidebar.help',
      name: 'help',
      layout: 'Student',
      breadcrumb: { name: 'home' },
    },
  },
  {
    path: '/games',
    name: 'games',
    component: () => import('@/views/games/index.vue'),
    hidden: true,
    meta: {
      roles: ['student'],
      layout: 'Student',
    },
  },
  {
    path: '/student',
    name: 'student',
    component: () => import('@/views/student/index.vue'),
    hidden: true,
    redirect: {
      name: 'studentProfile',
    },
    meta: {
      roles: ['student'],
      layout: 'Student',
    },
    children: [
      {
        path: 'profile',
        name: 'studentProfile',
        component: () => import('@/views/student/profile.vue'),
        meta: {
          roles: ['student'],
          backgroundColor: 'white',
          layout: 'Student',
          breadcrumb: { name: 'home' },
        },
      },
    ],
  },
  {
    path: '/',
    name: 'home',
    redirect: {
      name: 'students',
    },
    hidden: true,
    meta: {
      roles: ['tutor'],
      layout: 'Tutor',
    },
  },
  {
    path: '/profile',
    name: 'tutorProfile',
    component: () => import('@/views/tutor/index.vue'),
    hidden: true,
    meta: {
      roles: ['tutor'],
      backgroundColor: 'white',
      i18n: 'sidebar.viewProfile',
      name: 'tutorProfile',
      layout: 'Tutor',
    },
  },
  {
    path: '/profile/edit',
    name: 'profile-edit',
    component: () => import('@/views/tutor/components/ProfileEdit.vue'),
    hidden: true,
    meta: {
      roles: ['tutor'],
      layout: 'Tutor',
      backgroundColor: 'white',
    },
  },
  {
    path: '/students',
    name: 'students',
    component: () => import('@/views/students/index.vue'),
    meta: {
      roles: ['tutor'],
      icon: 'users.svg',
      backgroundColor: 'white',
      i18n: 'sidebar.yourChildren',
      name: 'Children',
      layout: 'Tutor',
    },
  },
  {
    path: '/settings',
    name: 'settings',
    component: () => import('@/views/settings/index.vue'),
    meta: {
      roles: ['tutor'],
      icon: 'settings.svg',
      backgroundColor: 'white',
      i18n: 'testBooks.settings',
      name: 'settings',
      layout: 'Tutor',
      breadcrumb: { name: 'students' },
    },
  },
  {
    path: '/help',
    name: 'help',
    component: () => import('@/views/help/index.vue'),
    meta: {
      roles: ['tutor'],
      icon: 'info.svg',
      backgroundColor: 'white',
      i18n: 'sidebar.help',
      name: 'help',
      layout: 'Tutor',
      breadcrumb: { name: 'home' },
    },
  },
  {
    path: '/manage-content-by-grade',
    name: 'manageContentByGrade',
    component: () => import('@/views/manage-content-by-grade/index.vue'),
    hidden: true,
    meta: {
      roles: ['tutor'],
      layout: 'Tutor',
      breadcrumb: { name: 'settings' },
      backgroundColor: 'white',
    },
  },
  // {
  //   path: '/manage-content-by-grade',
  //   name: 'manageContentByGrade',
  //   component: () => import('@/views/manage-content-by-grade/index.vue'),
  //   hidden: true,
  //   meta: {
  //     roles: ['tutor'],
  //     layout: 'Tutor',
  //     breadcrumb: { name: 'settings' },
  //     backgroundColor: 'white',
  //   },
  // },
  {
    path: '/evaluation',
    name: 'evaluation',
    component: () => import('@/views/evaluation/index.vue'),
    hidden: true,
    meta: {
      roles: ['tutor'],
      layout: 'Tutor',
      backgroundColor: 'white',
      breadcrumb: { name: 'settings' },
    },
    // children: [
    //   {
    //     path: 'home',
    //     name: 'evaluationHome',
    //     component: () => import('@/views/evaluation/home.vue'),
    //     meta: {
    //       roles: ['tutor'],
    //       layout: 'Tutor',
    //       breadcrumb: { name: 'settings' },
    //       backgroundColor: 'white',
    //     },
    //   },
    //   {
    //     path: 'start',
    //     name: 'evaluationStart',
    //     component: () => import('@/views/evaluation/test.vue'),
    //     meta: {
    //       roles: ['tutor'],
    //       layout: 'Tutor',
    //       backgroundColor: 'white',
    //     },
    //   },
    // ],
  },
  {
    path: '/',
    name: 'home',
    redirect: {
      name: 'signupTutor',
    },
    hidden: true,
    meta: {
      roles: ['installer'],
      layout: 'Tutor',
    },
  },
  {
    path: '/signup/student',
    name: 'signupStudent',
    component: () => import('@/views/signup/student/index.vue'),
    meta: {
      i18n: '',
      roles: ['installer'],
      backgroundColor: 'white',
      name: 'Create student',
      layout: 'Tutor',
    },
  },
  {
    path: '/signup/tutor',
    name: 'signupTutor',
    component: () => import('@/views/signup/tutor/index.vue'),
    meta: {
      i18n: '',
      roles: ['installer'],
      backgroundColor: 'white',
      name: 'Create tutor',
      layout: 'Tutor',
    },
  },

  // START OF PRACTICES ROUTES

  {
    path: '/practices',
    name: 'practices',
    hidden: true,
    component: () => import('@/views/practices/Home.vue'),
    meta: {
      i18n: '',
      name: 'Games',
      icon: 'laptop-computer.svg',
      roles: ['student'],
    },
  },
  {
    path: '/practices/clown-to-water',
    name: 'ClownToWater',
    hidden: true,
    component: () => import('@/views/practices/ClownToWater.vue'),
    meta: {
      roles: ['student'],
    },
  },
  {
    path: '/practices/easter-egg',
    name: 'EasterEgg',
    hidden: true,
    component: () => import('@/views/practices/EasterEgg.vue'),
    meta: {
      roles: ['student'],
    },
  },
  {
    path: '/practices/expanded-form',
    name: 'ExpandedForm',
    hidden: true,
    component: () => import('@/views/practices/ExpandedForm.vue'),
    meta: {
      roles: ['student'],
    },
  },
  {
    path: '/practices/fishy-problems',
    name: 'FishyProblems',
    hidden: true,
    component: () => import('@/views/practices/FishyProblems.vue'),
    meta: {
      roles: ['student'],
    },
  },
  {
    path: '/practices/pet-life',
    name: 'PetLife',
    hidden: true,
    component: () => import('@/views/practices/PetLife.vue'),
    meta: {
      roles: ['student'],
    },
  },
  {
    path: '/practices/pumpkin-clock',
    name: 'PumpkinClock',
    hidden: true,
    component: () => import('@/views/practices/PumpkinClock.vue'),
    meta: {
      roles: ['student'],
    },
  },
  {
    path: '/practices/saving-money',
    name: 'SavingMoney',
    hidden: true,
    component: () => import('@/views/practices/SavingMoney.vue'),
    meta: {
      roles: ['student'],
    },
  },
  {
    path: '/practices/soccer-goal',
    name: 'SoccerGoal',
    hidden: true,
    component: () => import('@/views/practices/SoccerGoal.vue'),
    meta: {
      roles: ['student'],
    },
  },

  // END OF PRACTICES ROUTES
];

const createRouter = () =>
  new Router({
    // mode: 'history', // require service support
    scrollBehavior: () => ({ y: 0 }),
    routes: constantRoutes,
  });

const router = createRouter();

export default router;
